import type { Account, AccountApiPort, AccountRoles, ProfileChanges, SignIn, SyncRequest, SyncResponse } from '../ports/account';
import { getHttpServerUrl } from './server-url.js';
import { fetchJson, jsonRequest } from './http-errors.js';

/** Syncing a batch of replays can take a while on a slow connection. */
const SYNC_TIMEOUT_MS = 60_000;

/** The account and sync endpoints (`/api/auth/*`, `/api/account`, `/api/sync`). */
export class HttpAccountApi implements AccountApiPort {
  constructor(
    private readonly baseUrl: string = getHttpServerUrl(),
    private readonly fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
  ) {}

  register(username: string, password: string): Promise<SignIn> {
    return this.request('/api/auth/register', 'POST', { username, password });
  }

  login(username: string, password: string): Promise<SignIn> {
    return this.request('/api/auth/login', 'POST', { username, password });
  }

  logout(token: string): Promise<void> {
    return this.request('/api/auth/logout', 'POST', undefined, token);
  }

  logoutEverywhere(token: string): Promise<void> {
    return this.request('/api/auth/logout-everywhere', 'POST', undefined, token);
  }

  me(token: string) {
    return this.request<{ account: SignIn['account']; usage: { bytes: number; items: number } }>('/api/account', 'GET', undefined, token);
  }

  roles(token: string): Promise<AccountRoles> {
    return this.request<AccountRoles>('/api/account/roles', 'GET', undefined, token);
  }

  updateProfile(token: string, changes: ProfileChanges): Promise<{ account: Account }> {
    return this.request<{ account: Account }>('/api/account', 'PATCH', changes, token);
  }

  changePassword(token: string, current: string, next: string): Promise<void> {
    return this.request('/api/account/password', 'POST', { current, next }, token);
  }

  deleteAccount(token: string, password: string): Promise<void> {
    return this.request('/api/account', 'DELETE', { password }, token);
  }

  sync(token: string, request: SyncRequest): Promise<SyncResponse> {
    return this.request('/api/sync', 'POST', request, token, SYNC_TIMEOUT_MS);
  }

  private request<T>(path: string, method: string, body?: unknown, token?: string, timeoutMs?: number): Promise<T> {
    return fetchJson<T>(this.fetchImpl, `${this.baseUrl}${path}`, jsonRequest({ method, body, token }), timeoutMs);
  }
}
